// Lead capture for the homepage / instant-quote form (booth-form.html) and
// the brochure download (brochure-form.html). Both used to create a BoothBook
// lead through a hidden iframe; now they POST to the worker's /quick-lead,
// which makes a lead in the CRM (owner, 2026-10-30).
//
// Each form has data-quick-lead, data-worker-url, data-turnstile-key and an
// empty [data-quick-lead-turnstile] placeholder. The brochure form is in the
// nav on every page, so Turnstile only loads once someone starts filling a
// form in, and renders invisibly ("interaction-only": a box only shows when
// Cloudflare wants a check). Typing the rest of the form gives it time.
//
// The forms' inline scripts call window.sfbQuickLead(form, fields): see
// sendQuickLead for what it resolves to.

import { onReady } from "#public/utils/on-ready.js";

const FORM = "form[data-quick-lead]";
const PLACEHOLDER = "[data-quick-lead-turnstile]";
const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const MAX_WAIT_MS = 2500;
const WIDGETS = new Map();

let scriptAdded = false;
const loadTurnstile = () => {
  if (scriptAdded || window.turnstile) return;
  if (document.querySelector(`script[src^="${TURNSTILE_SRC.split("?")[0]}"]`)) {
    return;
  }
  scriptAdded = true;
  const script = document.createElement("script");
  script.src = TURNSTILE_SRC;
  script.async = true;
  document.head.appendChild(script);
};

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (form) => {
  const placeholder = form.querySelector(PLACEHOLDER);
  const sitekey = form.dataset.turnstileKey || "";
  if (!placeholder || !sitekey || WIDGETS.has(form)) return;
  WIDGETS.set(form, null);
  loadTurnstile();
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return WIDGETS.delete(form);
  try {
    const id = turnstile.render(placeholder, {
      sitekey,
      action: "quick-lead",
      appearance: "interaction-only",
      theme: "light",
    });
    WIDGETS.set(form, id);
  } catch (_e) {
    WIDGETS.delete(form);
  }
};

const tokenFor = (form) => {
  const id = WIDGETS.get(form);
  if (!window.turnstile || !id) return "";
  try {
    return window.turnstile.getResponse(id) || "";
  } catch (_e) {
    return "";
  }
};

// A local build has no worker URL: no lead, the customer carries on.
const workerUrlOf = (form) =>
  (form.dataset.workerUrl || "").trim().replace(/\/$/, "");

const post = (url, body) =>
  fetch(url, {
    method: "POST",
    // keepalive: the request survives the page moving on.
    keepalive: true,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  }).then(
    () => "sent",
    () => "sent",
  );

// Resolves "captcha" while the security check hasn't finished (the form
// asks the customer to wait a moment and press again), else "sent" or
// "skipped" once the lead is on its way: on the worker's answer, or after
// MAX_WAIT_MS at most. Never rejects; the customer always carries on.
const sendQuickLead = (form, fields) => {
  const workerUrl = workerUrlOf(form);
  if (!workerUrl) return Promise.resolve("skipped");
  const token = tokenFor(form);
  if (!token) {
    renderTurnstile(form);
    return Promise.resolve("captcha");
  }
  const honeypot = form.querySelector("[name='website']");
  const request = post(`${workerUrl}/quick-lead`, {
    ...fields,
    turnstile_token: token,
    website: honeypot ? honeypot.value : "",
  });
  const wait = new Promise((resolve) =>
    setTimeout(() => resolve("sent"), MAX_WAIT_MS),
  );
  return Promise.race([request, wait]);
};

window.sfbQuickLead = sendQuickLead;

onReady(() => {
  document.addEventListener("focusin", (e) => {
    const form = e.target?.closest?.(FORM);
    if (form) renderTurnstile(form);
  });
});
